"use client";

import React, { useState, useEffect, useRef } from "react";
import { Maximize2, Layers, Droplet, Sparkles } from "lucide-react";
import { ToggleGroup, ToggleGroupItem } from "@/components/ui/toggle-group";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Link } from "@/components/common/Link";
type UseRevealOnScrollOptions = {
  once?: boolean;
  threshold?: number | number[];
  rootMargin?: string;
};
function useRevealOnScroll<T extends HTMLElement = HTMLElement>({
  once = true,
  threshold = 0.1,
  rootMargin = "0px"
}: UseRevealOnScrollOptions = {}) {
  const ref = useRef<T | null>(null);
  const [isVisible, setIsVisible] = useState(false);
  useEffect(() => {
    const node = ref.current;
    if (!node) return;
    if (typeof window === "undefined" || typeof IntersectionObserver === "undefined") {
      setIsVisible(true);
      return;
    }
    const observer = new IntersectionObserver(entries => {
      const entry = entries[0];
      if (entry?.isIntersecting) {
        setIsVisible(true);
        if (once) observer.unobserve(entry.target);
      } else if (!once) {
        setIsVisible(false);
      }
    }, {
      threshold,
      rootMargin
    });
    observer.observe(node);
    return () => observer.disconnect();
  }, [once, rootMargin, threshold]);
  return {
    ref,
    isVisible
  };
}
const projects = [{
  id: 1,
  title: "Midnight Echoes",
  category: "Hip-Hop",
  mainImage: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1655264/img/discography_hiphop_album_main.png",
  description: "A gritty, atmospheric exploration of urban soundscapes. We focused on capturing the raw energy of the vocal performances while maintaining a polished, commercial low-end.",
  paperWeight: "Neumann U87, Shure SM7B",
  inkType: "SSL 4000G Console, LA-2A",
  finish: "Analog Summing & Tube Saturation"
}, {
  id: 2,
  title: "Velvet Horizon",
  category: "Indie",
  mainImage: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1655264/img/discography_indie_ep_main.png",
  description: "Dreamy textures and layered vocal harmonies define this breakthrough EP. Recorded in our Live Room A to capture the natural room resonance and organic drum tones.",
  paperWeight: "Coles 4038, AKG C414",
  inkType: "Neve 1073 Preamps",
  finish: "Natural Room Reverb & Tape Delay"
}, {
  id: 3,
  title: "Neon Pulse",
  category: "Electronic",
  mainImage: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1655264/img/discography_electronic_single_main.png",
  description: "High-fidelity synthesis and surgical precision. This project pushed our digital-analog hybrid workflow to the limits with complex routing and hardware processing.",
  paperWeight: "Moog One, Prophet-6",
  inkType: "Universal Audio Apollo x16",
  finish: "Digital Precision & Hardware Grit"
}, {
  id: 4,
  title: "Blue Note Sessions",
  category: "Jazz",
  mainImage: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1655264/img/discography_jazz_quartet_main.png",
  description: "A live-to-tape session capturing the spontaneous interplay of a world-class quartet. Minimal miking techniques were used to preserve the authentic stereo image.",
  paperWeight: "Royer R-121, Sennheiser MD421",
  inkType: "API 1608 Console",
  finish: "Live-to-Tape & Zero Latency"
}, {
  id: 5,
  title: "Static Dreams",
  category: "Indie",
  mainImage: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1655264/img/discography_indie_lp_main.png",
  description: "Lo-fi aesthetics met high-end production in this full-length LP. We utilized vintage ribbon mics and saturated preamps to create a warm, nostalgic sonic profile.",
  paperWeight: "AEA R84, Beyerdynamic M160",
  inkType: "Chandler Limited TG2",
  finish: "Vintage Saturation & Lo-fi Textures"
}, {
  id: 6,
  title: "Subterranean",
  category: "Electronic",
  mainImage: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1655264/img/discography_techno_ep_main.png",
  description: "Deep, driving techno designed for the club. The focus was on phase-coherent low-end and intricate percussion layers that cut through any sound system.",
  paperWeight: "Elektron Analog Rytm, 303",
  inkType: "Empirical Labs Distressor",
  finish: "Club-Ready Mastering & Phase Alignment"
}];
const projectImages = [{
  projectId: 1,
  imageUrl: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1655264/img/discography_hiphop_detail_studio.png"
}, {
  projectId: 1,
  imageUrl: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1655264/img/discography_hiphop_detail_gear.png"
}, {
  projectId: 2,
  imageUrl: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1655264/img/discography_indie_detail_drums.png"
}, {
  projectId: 3,
  imageUrl: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1655264/img/discography_electronic_detail_synth.png"
}, {
  projectId: 4,
  imageUrl: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1655264/img/discography_jazz_detail_piano.png"
}, {
  projectId: 4,
  imageUrl: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1655264/img/discography_jazz_detail_mic.png"
}, {
  projectId: 5,
  imageUrl: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1655264/img/discography_indie_detail_guitar.png"
}, {
  projectId: 6,
  imageUrl: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1655264/img/discography_techno_detail_rack.png"
}];
export default function VibrantPortfolios() {
  const [activeFilter, setActiveFilter] = useState("All");
  const {
    ref: headerRef,
    isVisible: isHeaderVisible
  } = useRevealOnScroll();
  const {
    ref: gridRef,
    isVisible: isGridVisible
  } = useRevealOnScroll({
    threshold: 0.05
  });
  const filteredProjects = activeFilter === "All" ? projects : projects.filter(p => p.category === activeFilter);
  return <section data-section-id="234526" id="our-discography" className="bg-background text-foreground py-24 overflow-hidden relative">
      {}
      <div className="absolute top-0 left-0 w-full h-full overflow-hidden pointer-events-none z-0">
        <div className="absolute -top-[20%] -right-[10%] w-[50%] h-[50%] rounded-full bg-primary/10 blur-[120px]" />
        <div className="absolute top-[40%] -left-[10%] w-[40%] h-[40%] rounded-full bg-accent/10 blur-[100px]" />
        <div className="absolute -bottom-[10%] right-[20%] w-[60%] h-[60%] rounded-full bg-primary/5 blur-[150px]" />
      </div>

      <div className="container mx-auto px-4 md:px-6 relative z-10">
        {}
        <div ref={headerRef} className={`flex flex-col md:flex-row md:items-end justify-between gap-8 mb-16 transition-all duration-1000 ease-out ${isHeaderVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-12"}`}>
          <div className="max-w-2xl">
            <h2 className="text-4xl md:text-6xl font-default font-bold tracking-tighter text-foreground mb-4">
              Our <span className="text-transparent bg-clip-text bg-gradient-to-r from-primary via-primary/80 to-accent">Discography</span>
            </h2>
            <p className="text-lg text-muted-foreground font-default">
              A curated selection of sonic journeys crafted at PulseWave. From chart-topping singles to experimental EPs, explore the high-fidelity results of our collaborative process.
            </p>
          </div>

          {}
          <div className="flex-shrink-0">
            <ToggleGroup type="single" value={activeFilter} onValueChange={value => value && setActiveFilter(value)} className="bg-secondary/50 p-1 rounded-full backdrop-blur-md border border-border/50">
              {["All", "Hip-Hop", "Indie", "Electronic", "Jazz"].map((filter, i) => <ToggleGroupItem key={filter} value={filter} data-index={i} className={`px-6 py-2 rounded-full transition-all font-default text-sm ${activeFilter === filter ? "bg-primary shadow-[0_0_15px_rgba(0,255,255,0.3)] text-primary-foreground font-bold" : "text-muted-foreground hover:text-foreground"}`}>
                  {filter}
                </ToggleGroupItem>)}
            </ToggleGroup>
          </div>
        </div>

        {}
        <div ref={gridRef} className={`columns-1 md:columns-2 lg:columns-3 gap-6 space-y-6 transition-all duration-1000 delay-200 ease-out ${isGridVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-12"}`}>
          {filteredProjects.map((project, i) => <div key={project.id} data-index={i} className="break-inside-avoid">
              <Dialog>
                <DialogTrigger asChild>
                  <div className="group relative cursor-pointer overflow-hidden rounded-3xl bg-card border border-border/30 shadow-lg hover:shadow-primary/10 transition-all duration-500">
                    <div className="aspect-auto overflow-hidden">
                      <img src={project.mainImage} alt={project.title} className="w-full h-auto object-cover transition-all duration-700 grayscale group-hover:grayscale-0 group-hover:scale-105" loading="lazy" />
                    </div>
                    
                    {}
                    <div className="absolute inset-0 bg-gradient-to-t from-background/90 via-background/40 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex flex-col justify-end p-6">
                      <div className="transform translate-y-4 group-hover:translate-y-0 transition-transform duration-300">
                        <span className="inline-block px-3 py-1 mb-3 text-[10px] font-bold uppercase tracking-widest text-primary-foreground bg-primary rounded-full">
                          {project.category}
                        </span>
                        <h3 className="text-2xl font-default font-bold text-foreground mb-1">{project.title}</h3>
                        <div className="flex items-center text-primary text-sm font-medium mt-2">
                          <Maximize2 className="w-4 h-4 mr-2" />
                          Session Details
                        </div>
                      </div>
                    </div>
                  </div>
                </DialogTrigger>

                {}
                <DialogContent className="max-w-5xl w-[95vw] max-h-[90vh] overflow-y-auto p-0 border-border/50 bg-background/95 backdrop-blur-2xl shadow-4xl rounded-3xl">
                  <div className="grid grid-cols-1 lg:grid-cols-5 gap-0">
                    
                    {}
                    <div className="lg:col-span-3 bg-card/50 p-6 md:p-8 border-r border-border/50">
                      <div className="rounded-2xl overflow-hidden border border-border/50 shadow-xl mb-6">
                        <img src={project.mainImage} alt={project.title} className="w-full h-auto object-cover" />
                      </div>
                      
                      {}
                      <div className="grid grid-cols-2 gap-4">
                        {projectImages.filter(img => img.projectId === project.id).map((img, idx) => <div key={idx} data-index={idx} className="rounded-xl overflow-hidden border border-border/50 shadow-md aspect-video">
                              <img src={img.imageUrl} alt={`${project.title} detail ${idx + 1}`} className="w-full h-full object-cover hover:scale-110 transition-transform duration-700" />
                            </div>)}
                      </div>
                    </div>

                    {}
                    <div className="lg:col-span-2 p-6 md:p-8 flex flex-col">
                      <DialogHeader className="text-left mb-8">
                        <div className="mb-4">
                          <span className="inline-block px-3 py-1 text-[10px] font-bold uppercase tracking-widest text-primary bg-primary/10 rounded-full border border-primary/20">
                            {project.category}
                          </span>
                        </div>
                        <DialogTitle className="text-3xl md:text-4xl font-default font-bold tracking-tighter text-foreground mb-4">
                          {project.title}
                        </DialogTitle>
                        <DialogDescription className="text-base md:text-lg text-muted-foreground font-default leading-relaxed">
                          {project.description}
                        </DialogDescription>
                      </DialogHeader>

                      <div className="space-y-6 flex-grow">
                        <h4 className="text-xs font-bold uppercase tracking-[0.2em] text-primary border-b border-border/50 pb-2 font-mono">
                          Technical Notes
                        </h4>
                        
                        <div className="grid grid-cols-1 gap-6">
                          <div className="flex items-start gap-4">
                            <div className="p-2 rounded-xl bg-primary/10 text-primary shrink-0 border border-primary/20">
                              <Layers className="w-5 h-5" />
                            </div>
                            <div>
                              <p className="text-xs font-bold uppercase tracking-wider text-foreground/70 font-mono">Microphones</p>
                              <p className="text-foreground font-default">{project.paperWeight}</p>
                            </div>
                          </div>
                          
                          <div className="flex items-start gap-4">
                            <div className="p-2 rounded-xl bg-accent/10 text-accent-foreground shrink-0 border border-accent/20">
                              <Droplet className="w-5 h-5" />
                            </div>
                            <div>
                              <p className="text-xs font-bold uppercase tracking-wider text-foreground/70 font-mono">Outboard Gear</p>
                              <p className="text-foreground font-default">{project.inkType}</p>
                            </div>
                          </div>
                          
                          <div className="flex items-start gap-4">
                            <div className="p-2 rounded-xl bg-primary/10 text-primary shrink-0 border border-primary/20">
                              <Sparkles className="w-5 h-5" />
                            </div>
                            <div>
                              <p className="text-xs font-bold uppercase tracking-wider text-foreground/70 font-mono">Processing</p>
                              <p className="text-foreground font-default">{project.finish}</p>
                            </div>
                          </div>
                        </div>
                      </div>

                      <div className="mt-10 pt-6 border-t border-border/50">
                        <Button asChild className="w-full h-14 text-base font-bold rounded-full shadow-[0_0_20px_rgba(0,255,255,0.2)] hover:shadow-[0_0_30px_rgba(0,255,255,0.4)] transition-all bg-primary text-primary-foreground">
                          <Link to="/ContactPage">Book Your Session</Link>
                        </Button>
                      </div>
                    </div>

                  </div>
                </DialogContent>
              </Dialog>
            </div>)}
        </div>
        
        {filteredProjects.length === 0 && <div className="text-center py-24">
            <p className="text-xl text-muted-foreground font-default">No recordings found in this genre.</p>
          </div>}
      </div>
    </section>;
}